<template>
  <div class="container">
    <!-- Scenario panel -->

    <ScenarioPanel v-if="!finishedState" :scenario="selectedScenario">
      <div class="row gx-3">
        <!-- Slider component to move the values up and down -->
        <div class="col-xl-7 border-1 rounded-3 mb-xl-0 mb-3">
          <img
            class="shadow-sm rounded-3 w-100 img-fluid"
            :src="pngSrc"
            :alt="currentAltTag"
          />
        </div>
        <div
          class="col-xl-5 d-flex flex-column rounded-3 justify-content-between"
        >
          <div class="bg-light rounded-3 shadow-sm p-3 h-100">
            <h3 class="fw-bold">{{ selectedStage.name }}</h3>
            <div v-if="hasStarted">
              <p class="p-3 fs-5 form-control text-start overflow-auto">
                {{ selectedStage.description }}
              </p>
            </div>
            <div v-else>
              <p class="p-3 fs-5 form-control text-start overflow-auto">
                {{ activityIntro.instructions }}
              </p>
            </div>
            <div>
              <div
                v-if="answeredCorrectly && hasAnswered"
                class="p-3 alert alert-success"
              >
                <p class="fs-5 text-start overflow-auto mb-0">
                  {{ selectedStage.correctFeedback }}
                </p>
              </div>
              <div
                v-else-if="answeredIncorrectly && hasAnswered"
                class="p-3 alert alert-danger"
              >
                <div v-if="selectedStage.incorrectFeedbackHigherRange">
                  <p
                    v-if="
                      selectedStage.incorrectFeedbackHigherRangeCriteria(
                        selectedScenarioSliderValue
                      )
                    "
                    class="fs-5 text-start overflow-auto mb-0"
                  >
                    {{ selectedStage.incorrectFeedbackHigherRange }}
                  </p>
                  <p v-else class="fs-5 text-start overflow-auto mb-0">
                    {{ selectedStage.incorrectFeedbackLowerRange }}
                  </p>
                </div>
                <p v-else class="fs-5 text-start overflow-auto mb-0">
                  {{ selectedStage.incorrectFeedback }}
                </p>
              </div>
            </div>
          </div>
          <div class="mt-3 shadow-sm bg-light rounded-3 p-1">
            <div v-if="hasStarted">
              <SliderComponent
                v-if="currentStage < stageLimit"
                @update:selectedScenarioSlider="
                  handleSelectedScenarioUpdate($event)
                "
                :slider="selectedScenario.sliders[currentStage]"
                :isDisabled="hasAnswered && answeredCorrectly"
              />

              <button
                v-if="
                  currentStage < stageLimit && hasAnswered && answeredCorrectly
                "
                class="btn btn-success btn-lg m-3 w-75 fw-bold"
                @click="changeStage(1)"
              >
                Continue
              </button>
              <button
                v-else-if="currentStage < stageLimit"
                class="btn btn-primary btn-lg m-3 w-75 fw-bold"
                @click="checkAnswer()"
              >
                Apply
              </button>
              <button
                v-if="
                  currentStage === stageLimit && currentScenario < scenarioLimit
                "
                class="btn btn-success btn-lg m-3 w-75 fw-bold"
                @click="changeScenario(1)"
              >
                Next Scenario
                <i class="bi bi-arrow-right"></i>
              </button>
              <button
                v-if="
                  currentStage === stageLimit &&
                  currentScenario === scenarioLimit
                "
                class="btn btn-success btn-lg m-3 w-75 fw-bold"
                @click="setFinishedState()"
              >
                Complete Activity
                <i class="bi bi-arrow-right"></i>
              </button>
            </div>
            <div v-else>
              <button
                class="btn btn-primary btn-lg m-3 w-75 fw-bold"
                @click="start()"
              >
                Start
              </button>
            </div>
          </div>
        </div>
      </div>
    </ScenarioPanel>
    <CertificateOfCompletion v-else class="my-3" />
  </div>
</template>

<script>
import SliderComponent from "@/components/activity/SliderComponent";
import ScenarioPanel from "@/components/activity/ScenarioPanel";
import CertificateOfCompletion from "@/components/ui/CertificateOfCompletion.vue";
import { scenariosData } from "@/data/scenarios.js";
import axios from "axios";
import { mapGetters, mapActions } from "vuex";
import DatastoreAPI from "@/helpers/DatastoreAPI";
const ALT_TAGS = "altTags";
export default {
  name: "ActivityView",
  mixins: [DatastoreAPI],
  data() {
    return {
      currentScenario: 0,
      currentStage: 0,
      showSlider: false,
      hasStarted: false,
      answeredCorrectly: false,
      answeredIncorrectly: true,
      hasAnswered: false,
      stageLimit: scenariosData[0].stages.length - 1, //* the limit is set by any of the stages
      scenarioLimit: scenariosData.length - 2, //* the limit is set by the number of scenarios not including the intro and completion scenarios
      scenarios: scenariosData,
      pngSrc: "",
      currentPostfixId: "",
      localSliderAnswers: {
        mist: 0,
        heat: 0,
        light: 0
      },
      finishedState: false,
      startScenario: 0,
      endScenario: 4
    };
  },
  components: {
    SliderComponent,
    ScenarioPanel,
    CertificateOfCompletion
  },
  computed: {
    ...mapGetters(["isDebugMode"]),
    ...mapGetters(["getSavedLocation", "mapToDatastore"]),
    activityIntro() {
      return this.scenarios.find((scenario) => scenario.id === "intro");
    },
    selectedScenario() {
      return this.scenarios[this.currentScenario];
    },
    selectedStage() {
      return this.selectedScenario.stages[this.currentStage];
    },
    currentAltTag() {
      let altTag = "";
      if (Object.hasOwn(this.selectedScenario, ALT_TAGS)) {
        const altTagFound = this.selectedScenario.altTags.find(
          (tag) => tag.id === this.currentPostfixId
        );
        if (altTagFound) {
          altTag = altTagFound.altTag;
        }
      }
      return altTag;
    },
    selectedScenarioSliderValue() {
      return this.selectedScenario.sliders.find(
        (slider) => slider.id === "light"
      ).value;
    }
  },
  methods: {
    ...mapActions(["setSavedLocation"]),
    //* Resets answered scenario.
    reset() {
      this.resetAnswerState();
      this.resetLocalSliderAnswers();
      this.currentStage = 0;
      this.setSavedLocation(this.currentScenario);
      this.loadPng();
    },
    setFinishedState() {
      this.finishedState = !this.finishedState;
    },
    syncSavedLocation() {
      this.currentScenario = this.getSavedLocation;
    },
    rootMistPostfix() {
      const mistSlider = this.selectedScenario.sliders.find(
        (slider) => slider.id === "mist"
      );
      let answeredCorrectly = false;
      if (this.selectedStage.correctAnswerCriteria) {
        answeredCorrectly = this.selectedStage.correctAnswerCriteria(
          Number(mistSlider.value)
        );
      } else {
        answeredCorrectly =
          Number(mistSlider.value) >= Number(this.selectedStage.correctAnswer);
      }

      if (answeredCorrectly) {
        this.localSliderAnswers.mist = this.selectedStage.correctAnswer;
      } else {
        this.localSliderAnswers.mist = this.selectedStage.incorrectAnswer;
      }
    },
    rootHeatPostfix() {
      const heatSlider = this.selectedScenario.sliders.find(
        (slider) => slider.id === "heat"
      );
      let answeredCorrectly = null;
      if (this.selectedStage.correctAnswerCriteria) {
        answeredCorrectly = this.selectedStage.correctAnswerCriteria(
          heatSlider.value
        );
      }
      if (answeredCorrectly) {
        this.localSliderAnswers.heat = this.selectedStage.correctAnswer;
      } else {
        this.localSliderAnswers.heat = this.selectedStage.incorrectAnswer;
      }
    },
    rootLightPostfix() {
      const lightSlider = this.selectedScenario.sliders.find(
        (slider) => slider.id === "light"
      );
      let answeredCorrectly = false;

      const correctAnswer = this.selectedStage.correctAnswer;
      if (typeof correctAnswer === "number") {
        answeredCorrectly = this.selectedStage.correctAnswerCriteria(
          lightSlider.value
        );
      }

      if (answeredCorrectly) {
        this.localSliderAnswers.light = this.selectedStage.correctAnswer;
      } else {
        this.localSliderAnswers.light = this.selectedStage.incorrectAnswer;
      }
    },
    updatePostfixes() {
      if (this.selectedStage.id === "mist") {
        this.rootMistPostfix();
      }
      if (this.selectedStage.id === "heat") {
        this.rootHeatPostfix();
      }
      if (this.selectedStage.id === "light") {
        this.rootLightPostfix();
      }
    },
    async handleSpecialCase() {
      let imagePath = null;
      if (
        this.selectedStage.incorrectFeedbackHigherRangeCriteria(
          this.selectedScenarioSliderValue
        )
      ) {
        imagePath = await import(
          `@/assets/scenario-${this.currentScenario}-assets/root111.png`
        );
      } else if (
        this.selectedStage.incorrectFeedbackLowerRangeCriteria(
          this.selectedScenarioSliderValue
        )
      ) {
        imagePath = await import(
          `@/assets/scenario-${this.currentScenario}-assets/root101.png`
        );
      } else {
        imagePath = await import(
          `@/assets/scenario-${this.currentScenario}-assets/root${this.localSliderAnswers.mist}${this.localSliderAnswers.heat}${this.localSliderAnswers.light}.png`
        );
      }
      return imagePath;
    },

    async loadPng() {
      try {
        let response = null;
        let imagePath = null;
        if (this.currentStage === 0) {
          if (this.hasAnswered) {
            imagePath = await import(
              `@/assets/scenario-${this.currentScenario}-assets/root${this.localSliderAnswers.mist}.png`
            );
          } else {
            imagePath = await import(
              `@/assets/scenario-${this.currentScenario}-assets/root.png`
            );
          }
        }
        if (this.currentStage === 1) {
          imagePath = await import(
            `@/assets/scenario-${this.currentScenario}-assets/root${this.localSliderAnswers.mist}${this.localSliderAnswers.heat}.png`
          );
        }
        if (this.currentStage === 2) {
          //* We need to account for the custom range of the light slider
          //* Neat short way to check if an object has a specific key: https://stackoverflow.com/questions/135448/how-do-i-check-if-an-object-has-a-specific-property-in-javascript
          if (
            Object.hasOwn(
              this.selectedStage,
              "incorrectFeedbackHigherRangeCriteria"
            ) &&
            Object.hasOwn(
              this.selectedStage,
              "incorrectFeedbackLowerRangeCriteria"
            )
          ) {
            imagePath = await this.handleSpecialCase();
          } else {
            imagePath = await import(
              `@/assets/scenario-${this.currentScenario}-assets/root${this.localSliderAnswers.mist}${this.localSliderAnswers.heat}${this.localSliderAnswers.light}.png`
            );
          }
        }
        if (this.currentStage > 2) {
          imagePath = await import(
            `@/assets/scenario-${this.currentScenario}-assets/root${this.localSliderAnswers.mist}${this.localSliderAnswers.heat}${this.localSliderAnswers.light}.png`
          );
        }
        const imageUrl = imagePath.default;
        response = await axios.get(
          imageUrl, // Use require to get the image path
          { responseType: "blob" } // Fetch the image as a Blob
        );

        this.currentPostfixId = imagePath.default
          .split(".")[0]
          .split("/")
          .pop();

        // Create a URL for the Blob and assign it to a component property
        this.pngSrc = URL.createObjectURL(response.data);
      } catch (error) {
        console.error("Error loading PNG:", error);
      }
    },
    //* In most programming languages doing comparison of floating point numbers can be tricky due to how they are usually represented in binary.
    //* For that reason, we must be precise about where we round.
    areLessThan(a, b, precision = 3) {
      return (
        Number.parseFloat(a).toFixed(precision) <
        Number.parseFloat(b).toFixed(precision)
      );
    },
    areGreaterThan(a, b, precision = 3) {
      return (
        Number.parseFloat(a).toFixed(precision) >
        Number.parseFloat(b).toFixed(precision)
      );
    },
    resetAnswerState() {
      this.answeredCorrectly = false;
      this.answeredIncorrectly = true;
      this.hasAnswered = false;
    },
    start() {
      this.hasStarted = !this.hasStarted;
    },

    // * the changing of the scenario is circular
    changeScenario(direction) {
      this.currentStage = 0; //* we need to reset the current stage state when we change scenarios
      this.currentScenario += direction;
      this.resetLocalSliderAnswers();
      this.loadPng();
      this.setSavedLocation(this.currentScenario);
      this.updateEntry(this.mapToDatastore);
    },
    resetLocalSliderAnswers() {
      this.localSliderAnswers = { mist: 0, heat: 0, light: 0 };
    },

    handleNumberCheckAnswer(currentSlider) {
      this.answeredCorrectly = this.selectedStage.correctAnswerCriteria(
        currentSlider.value
      );
      this.answeredIncorrectly = !this.answeredCorrectly;
    },
    checkAnswer() {
      const currentSlider = this.selectedScenario.sliders[this.currentStage];
      this.handleNumberCheckAnswer(currentSlider);
      this.hasAnswered = true;
      this.updatePostfixes();
      this.loadPng();
    },
    startStage() {
      this.showSlider = true;
    },
    changeStage(direction) {
      this.currentStage += direction;
      this.hasAnswered = false;
      if (this.currentStage > this.selectedScenario.stages.length - 1) {
        this.currentStage = 0;
      } else if (this.currentStage < 0) {
        this.currentStage = this.selectedScenario.stages.length - 1;
      }
    },
    //* update the selected scenario slider with the new value/s
    handleSelectedScenarioUpdate(slider) {
      const updatedSliders = this.selectedScenario.sliders.map((s) => {
        if (s.id === slider.id) {
          return slider;
        }
        return s;
      });
      //* we are going to need to stick to still a binary system where if it is past a threshold we still only show one of two images
      //* so that if we have a plant with mist more than or equal to 75% then the value of the slider will be changed to 1, and if it is less than 75% then we change the value of the slider to 0.
      this.selectedScenario.sliders = updatedSliders;
      this.hasAnswered = false;
    }
  },
  watch: {
    getSavedLocation(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.syncSavedLocation();
      }
    }
  },
  mounted() {
    this.syncSavedLocation();
    this.loadPng();
  }
};
</script>
